// Cyberpunk skin: a courier sprints a rain-slick street to the data-spire.
//   idle    - courier idles by the spire under the neon
//   pending - courier runs; progress eases them toward the tower
//   failure - courier freezes and a sentry drone blocks the street
//   success - the data-spire lights green
import { makeScene, mix, type Palette, type SceneCtx } from '../scene/engine';
import type { SkinDef } from './types';

const colors = (g: (n: string) => string): Palette => ({
  skyTop: mix(g('--bg'), g('--violet'), 0.16), skyBot: g('--bg-raise'),
  ground: g('--surface'), groundLo: g('--bg'), edge: g('--line-bright'), dash: g('--line'),
  accent: g('--accent'), neon2: g('--violet'), green: g('--green'), red: g('--red'), amber: g('--amber'),
  metal: '#3a4150', metalHi: '#5b6478', glass: '#0c1420', dark: '#08080f',
  skin: '#e9cfa0', visor: '#c8f7ff',
  droneBody: '#2a2f3c', droneHi: '#4a5266',
});

// Distant neon signs on the skyline.
function skyline(s: SceneCtx, C: Palette) {
  const { px, groundY: GY } = s;
  px(18, GY - 12, 10, 12, C.glass); px(20, GY - 9, 2, 2, C.neon2); px(24, GY - 6, 2, 2, C.accent);
  px(120, GY - 15, 12, 15, C.glass); px(123, GY - 12, 2, 3, C.accent); px(127, GY - 8, 2, 2, C.neon2);
}
function streetlight(s: SceneCtx, C: Palette, x: number, f: number) {
  const { px, groundY: GY } = s;
  px(x, GY - 15, 2, 15, C.metal); px(x - 3, GY - 16, 6, 2, C.metal);
  px(x - 3, GY - 14, 6, 1, f % 2 ? C.accent : mix(C.accent, C.glass, 0.35)); // buzzing lamp
}
function rain(s: SceneCtx, C: Palette, f: number) {
  const { px, groundY: GY, W } = s;
  for (let x = (f * 7) % 13; x < W; x += 13) px(x, ((x * 5) % (GY - 6)), 1, 3, mix(C.accent, C.skyBot, 0.55));
}
function spire(s: SceneCtx, C: Palette, green: boolean) {
  const { px, glow, groundY: GY, W } = s; const bx = W - 34, base = GY;
  if (green) glow(bx - 8, base - 40, 34, 44, C.green, 0.16);
  px(bx, base - 30, 20, 30, C.glass);                          // tower body
  px(bx, base - 30, 20, 1, C.metalHi); px(bx, base - 30, 1, 30, C.metal); px(bx + 19, base - 30, 1, 30, C.metal);
  const lit = green ? C.green : C.accent, lit2 = green ? C.green : C.neon2;
  for (let r = 0; r < 6; r++) for (let c = 0; c < 3; c++) px(bx + 3 + c * 6, base - 27 + r * 5, 3, 3, (r + c) % 2 ? lit : lit2);
  px(bx + 9, base - 38, 2, 8, C.metalHi);                      // antenna
  px(bx + 8, base - 40, 4, 2, green ? C.green : C.red);        // beacon
}
function courier(s: SceneCtx, C: Palette, x: number, walkF: number, tint: string, resting: boolean) {
  const { px, groundY: GY } = s;
  const fy = GY + 2, bob = !resting && walkF % 2 ? -1 : 0, y = fy - 16 + bob;
  // legs (running stride)
  if (resting) { px(x + 3, fy - 4, 2, 4, C.dark); px(x + 7, fy - 4, 2, 4, C.dark); }
  else { const st = walkF % 2 ? 2 : -2; px(x + 3 - st, fy - 4, 2, 4, C.dark); px(x + 7 + st, fy - 4, 2, 4, C.dark); }
  // jacket + accent trim, forward lean hinted by an offset collar
  px(x + 3, y + 7, 7, 7, C.metal); px(x + 3, y + 7, 7, 1, tint); px(x + 4, y + 9, 5, 1, tint);
  px(x + 2, y + 8, 1, 5, tint);                                 // trailing scarf/edge glow
  // head + visor
  px(x + 3, y + 1, 6, 6, C.skin); px(x + 3, y + 2, 6, 2, C.visor);
  px(x + 3, y, 6, 2, C.dark);                                   // cap
  // arm / satchel
  if (resting) px(x + 8, y + 9, 3, 4, C.metalHi);
  else px(x + 9, y + 8, 2, 4, C.skin);
  px(x + 9, y + 11, 4, 4, tint);                               // glowing courier satchel
}
function drone(s: SceneCtx, C: Palette, x: number) {
  const { px, groundY: GY } = s; const y = GY - 12;
  px(x - 5, y, 5, 1, C.droneHi); px(x + 10, y, 5, 1, C.droneHi);  // rotor arms
  px(x - 6, y - 1, 3, 1, C.metalHi); px(x + 13, y - 1, 3, 1, C.metalHi);
  px(x + 1, y + 1, 8, 6, C.droneBody); px(x + 1, y + 1, 8, 1, C.droneHi);
  px(x + 2, y + 3, 6, 2, C.red); px(x + 4, y + 3, 2, 2, '#fff');  // red scanner eye
}

const draw = (C: Palette, s: SceneCtx) => {
  const { px, mix: m, W, H, groundY: GY, pathL: PL, state: st, heroX: hx, walkF, flameF } = s;
  const tint = st === 'failure' ? C.red : st === 'success' ? C.green : C.accent;
  const allDone = st === 'success';
  // sky
  for (let y = 0; y < GY; y++) px(0, y, W, 1, m(C.skyTop, C.skyBot, y / GY));
  skyline(s, C);
  if (walkF || flameF) rain(s, C, walkF + flameF);
  // wet street
  for (let y = GY; y < H; y++) px(0, y, W, 1, m(C.ground, C.groundLo, (y - GY) / (H - GY)));
  px(0, GY - 1, W, 2, C.edge);
  px(0, GY + 6, W, 1, m(C.accent, C.ground, 0.7));            // faint neon reflection on the asphalt
  // lane dashes + trodden neon trail
  const progX = st === 'idle' ? PL : hx + 6;
  for (let x = PL; x < W - 8; x += 10) { const done = allDone || x < progX; px(x, GY + 3, 6, 2, done ? tint : C.dash); }
  px(PL, GY + 2, allDone ? W - 8 - PL : Math.max(0, progX - PL), 1, m(tint, C.ground, 0.3));
  streetlight(s, C, 30, walkF); streetlight(s, C, 104, walkF + 1); streetlight(s, C, 166, walkF + 2);
  spire(s, C, st === 'success');
  courier(s, C, hx, walkF, tint, st === 'idle');
  if (st === 'failure') drone(s, C, hx + 13);
};

export const cyberpunk: SkinDef = {
  label: 'Cyberpunk',
  PromoteScene: makeScene({ colors, draw }),
  lines: {
    pending: 'Jacking in — hold my ping.',
    failure: 'ICE on the wire. Hard stop.',
    success: 'Spire’s green. We’re in. ⚡',
  },
};
